/* eslint-disable @typescript-eslint/no-misused-promises */
import React, { useState } from 'react'
import { CircularProgress, Autocomplete, Box, InputLabel, TextField } from '@mui/material'
import _, { isEmpty } from 'lodash'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { type ISection } from '../../../../../models/TermsSection'
import api from '../../../../../utils/api'
import { grey } from '../../../../../colors'

interface CrossListCodeSectionDisplayProps {
  crossListCode?: string
  termId?: string
}

export const CrossListCodeSectionDisplay: React.FC<CrossListCodeSectionDisplayProps> = ({
  crossListCode,
  termId
}) => {
  const [loading, setLoading] = useState<boolean>(false)
  const { data: sectionList } = useQuery<ISection[]>({
    queryKey: ['crosslistedsectionList' + (termId ?? ''), crossListCode],
    placeholderData: keepPreviousData,
    staleTime: 100000,
    queryFn: async (): Promise<ISection[]> => {
      setLoading(true)
      try {
        if (!crossListCode || !termId) {
          // disable search if no termId
          return []
        }
        const result = await api.get('/api/sections/', {
          params: {
            termId,
            pageSize: 10,
            filters: crossListCode !== undefined
              ? JSON.stringify({
                field: 'cross_list_code',
                op: '==',
                value: crossListCode
              })
              : undefined,
            sort: JSON.stringify({
              field: 'abbreviation',
              direction: 'asc'
            })
          }
        })
        setLoading(false)
        return result.data
      } finally {
        setLoading(false)
      }
    }
  })

  const sectionListAbbr = sectionList?.map(s => s.abbreviation) ?? []

  let placeholder: string | undefined = 'Cross-Listed Courses'

  if (!isEmpty(sectionListAbbr)) {
    placeholder = undefined
  } else if (!isEmpty(crossListCode) && !loading && isEmpty(sectionListAbbr)) {
    placeholder = 'No Matching Cross-Listed Courses'
  }

  return (
    <Box>
      <InputLabel sx={{ marginBottom: 2, color: grey[950], fontSize: '14px' }}>
        Cross-Listed Courses
      </InputLabel>
      <Autocomplete
        freeSolo
        multiple
        options={sectionListAbbr}
        value={sectionListAbbr}
        readOnly
        filterOptions={(options) => options}
        size="small"

        renderInput={({ InputProps, ...params }) => (
          <TextField {...params} disabled
            InputProps={{
              ...InputProps,
              endAdornment: (
                <React.Fragment>
                  {loading ? <CircularProgress color="inherit" size={20} /> : null}
                  {InputProps.endAdornment}
                </React.Fragment>
              )
            }}
            placeholder={placeholder} />
        )}
      />
    </Box>
  )
}
